import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import type { MouseEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import type { AvailableColumn } from '../types'
import { ColumnPickerCategories } from './ColumnPickerCategories'
import { ColumnPickerDynamicColumns } from './ColumnPickerDynamicColumns'
import { ColumnPickerStaticColumns } from './ColumnPickerStaticColumns'
import messages from './messages'
import * as S from './styles'
import type { ColumnCategory } from './useColumns'

interface ColumnPickerProps {
  staticColumns: AvailableColumn[]
  dynamicColumns?: AvailableColumn[] | null | undefined
  selectedColumns: string[]
  columnCategories?: ColumnCategory[]
  onToggle: (columnId: string) => void
}

export const ColumnPicker = ({
  staticColumns,
  selectedColumns,
  columnCategories,
  dynamicColumns,
  onToggle,
}: ColumnPickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setIsExpanded(false)
    setAnchorEl(null)
  }

  const isOpen = Boolean(anchorEl)

  return (
    <>
      <S.PickerTrigger>
        <IconButton
          data-test-id="open-column-picker-button"
          onClick={handleClick}
          size="small"
          sx={{
            backgroundColor: isOpen ? 'action.selected' : undefined,
            borderColor: isOpen ? 'divider' : undefined,
          }}
          title="Pick columns"
        >
          <ViewColumnIcon />
        </IconButton>
      </S.PickerTrigger>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        slotProps={{
          paper: {
            sx: {
              background: 'none',
              boxShadow: 'none',
              zIndex: 1,
            },
          },
        }}
      >
        <S.PopoverContent isExpanded={isExpanded}>
          {isExpanded && columnCategories && (
            <ColumnPickerCategories
              columnCategories={columnCategories}
              selectedColumns={selectedColumns}
              onToggle={onToggle}
              onClose={() => setIsExpanded(false)}
            />
          )}
          <S.ColumnsPanel isExpanded={isExpanded}>
            <S.SectionHeading>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage {...messages.column} />
              </Typography>
            </S.SectionHeading>
            <ColumnPickerStaticColumns
              staticColumns={staticColumns}
              handleToggle={onToggle}
              selectedColumns={selectedColumns}
            />
            {columnCategories && (
              <ColumnPickerDynamicColumns
                dynamicColumns={dynamicColumns}
                setExpanded={setIsExpanded}
                onToggle={onToggle}
              />
            )}
          </S.ColumnsPanel>
        </S.PopoverContent>
      </Popover>
    </>
  )
}
